import { Link } from 'react-router-dom';
import { DEFAULT_STATE } from '../../utils/states';

// Arizona's guides were the only ones when this was written, so the layout hardcoded
// Arizona's breadcrumb, package card and section heading. Those are now props whose
// defaults reproduce the Arizona output exactly — passing `state` swaps the lot.
const AZ_CTA_POINTS = [
  'Every required category',
  '2025 Water, Firewise & Deed Fraud included',
  '100% online, self-paced',
  'Instant certificates',
];

export default function ResourceLayout({
  breadcrumb,
  title,
  subtitle,
  updatedLabel = 'Updated June 2026 · Verified against azre.gov',
  children,
  relatedGuides = [],
  state = DEFAULT_STATE,
  // Crumbs between Home and this page. Must mirror the JSON-LD BreadcrumbList —
  // Google expects the marked-up trail to match the one a reader can see.
  crumbs = [{ label: 'Arizona', href: '/arizona' }, { label: 'Resources', href: '/arizona/resources' }],
  ctaPoints = AZ_CTA_POINTS,
  ctaSub = 'All 24 ADRE-required hours',
}) {
  return (
    <div style={styles.page}>
      {/* Branded hero */}
      <div style={styles.hero}>
        <div className="container">
          <div style={styles.crumb}>
            <Link to="/" style={styles.crumbLink}>Home</Link>
            {crumbs.map(c => (
              <span key={c.href} style={{ display: 'contents' }}>
                <span style={styles.crumbSep}>›</span>
                <Link to={c.href} style={styles.crumbLink}>{c.label}</Link>
              </span>
            ))}
            {breadcrumb && <>
              <span style={styles.crumbSep}>›</span>
              <span>{breadcrumb}</span>
            </>}
          </div>
          <h1 style={styles.heroTitle} className="resource-hero-title">{title}</h1>
          {subtitle && <p style={styles.heroSubtitle}>{subtitle}</p>}
          <div style={styles.updatedBadge}>{updatedLabel}</div>
        </div>
      </div>

      {/* Two-column body */}
      <div className="container resource-body" style={styles.body}>
        <div style={styles.articleCol}>
          <article style={styles.article} className="resource-article">{children}</article>
        </div>

        <aside style={styles.sidebarCol} className="resource-sidebar">
          <div style={styles.ctaCard}>
            <div style={styles.ctaTag}>⭐ Best Value</div>
            <div style={styles.ctaTitle}>Complete {state.name} CE Package</div>
            <div style={styles.ctaPrice}>${state.packagePrice}</div>
            <div style={styles.ctaSub}>{ctaSub}</div>
            <ul style={styles.ctaList}>
              {ctaPoints.map(p => <li key={p}>✓ {p}</li>)}
            </ul>
            <Link to={`/${state.slug}/courses`} style={styles.ctaBtn}>Get the package →</Link>
            <div style={styles.ctaNote}>🔒 Secure checkout via Stripe</div>
          </div>
        </aside>
      </div>

      {/* Related guides */}
      {relatedGuides.length > 0 && (
        <div style={styles.relatedWrap}>
          <div className="container">
            <h2 style={styles.relatedTitle}>More {state.name} CE Guides</h2>
            <div style={styles.relatedGrid}>
              {relatedGuides.map(g => (
                <Link key={g.url} to={g.url} style={styles.relatedCard}>
                  <div style={styles.relatedCardTitle}>{g.title}</div>
                  <div style={styles.relatedCardDesc}>{g.desc}</div>
                  <div style={styles.relatedCardArrow}>Read guide →</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { background: '#FAF7F2', minHeight: '100vh' },
  hero: { background: 'linear-gradient(135deg, #2C453E 0%, #243A34 100%)', color: '#EFE6D6', padding: '40px 0 56px' },
  crumb: { fontSize: 13, color: 'rgba(239,230,214,0.7)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  crumbLink: { color: 'rgba(239,230,214,0.85)', textDecoration: 'none' },
  crumbSep: { opacity: 0.5 },
  heroTitle: { fontFamily: 'var(--font-display)', fontSize: 36, lineHeight: 1.15, marginBottom: 12, color: 'white' },
  heroSubtitle: { fontSize: 17, lineHeight: 1.6, maxWidth: 720, color: 'rgba(239,230,214,0.88)', marginBottom: 20 },
  updatedBadge: { display: 'inline-block', fontSize: 12, color: '#B6A189', background: 'rgba(182,161,137,0.12)', border: '1px solid rgba(182,161,137,0.35)', padding: '4px 12px', borderRadius: 999, fontStyle: 'italic' },
  body: { display: 'grid', gridTemplateColumns: '1fr 320px', gap: 40, padding: '40px 0', alignItems: 'start' },
  articleCol: { minWidth: 0 },
  article: { background: 'white', borderRadius: 16, padding: '40px 44px', border: '1px solid #e8e2d6', boxShadow: '0 1px 2px rgba(36,58,52,0.04)', fontSize: 16, lineHeight: 1.75, color: '#243A34' },
  sidebarCol: { position: 'sticky', top: 88 },
  ctaCard: { background: 'linear-gradient(135deg, #2C453E, #243A34)', color: 'white', borderRadius: 16, padding: 24, boxShadow: '0 8px 24px rgba(36,58,52,0.15)' },
  ctaTag: { display: 'inline-block', background: 'rgba(182,161,137,0.25)', color: '#d4b896', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, marginBottom: 10, border: '1px solid rgba(182,161,137,0.4)' },
  ctaTitle: { fontFamily: 'var(--font-display)', fontSize: 20, lineHeight: 1.25, marginBottom: 8 },
  ctaPrice: { fontSize: 42, fontWeight: 800, lineHeight: 1 },
  ctaSub: { fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 16 },
  ctaList: { listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'rgba(255,255,255,0.85)' },
  ctaBtn: { display: 'block', background: '#B6A189', color: 'white', textAlign: 'center', textDecoration: 'none', fontWeight: 700, padding: '12px 16px', borderRadius: 10, fontSize: 15 },
  ctaNote: { fontSize: 11, color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginTop: 10 },
  relatedWrap: { background: 'white', padding: '40px 0 56px', borderTop: '1px solid #e8e2d6' },
  relatedTitle: { fontFamily: 'var(--font-display)', fontSize: 24, color: '#243A34', marginBottom: 20 },
  relatedGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 },
  relatedCard: { background: '#FAF7F2', border: '1px solid #e8e2d6', borderRadius: 12, padding: 20, textDecoration: 'none', color: '#243A34', transition: 'all 0.15s' },
  relatedCardTitle: { fontWeight: 700, fontSize: 16, marginBottom: 6, lineHeight: 1.3 },
  relatedCardDesc: { fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.5, marginBottom: 12 },
  relatedCardArrow: { fontSize: 12, fontWeight: 600, color: '#2C453E' },
};
